import React, { useState } from 'react';
import { BaseModal } from '../ui/modal';
import { useAuth } from '../../context/AuthContext';

interface EmailVerificationModalProps {
  isOpen: boolean;
  actionName: string;
  actionDescription: string;
  onClose: () => void;
  onVerificationComplete: () => void;
}

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  actionName,
  actionDescription,
  onClose,
  onVerificationComplete
}) => {
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const { user } = useAuth();

  if (!user) return null;

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendMessage(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: user.email }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendMessage('Email de verificación enviado correctamente');
      } else {
        setResendMessage(`Error: ${data.message || 'Error al enviar email'}`);
      }
    } catch (error) {
      setResendMessage('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setIsResending(false);
      
      // Clear message after 5 seconds
      setTimeout(() => setResendMessage(null), 5000);
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} size="sm">
        {/* Header */}
        <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
              <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Verificación Requerida
            </h3>
        </div>

        {/* Content */}
        <div>
          <div className="mb-4">
            <h4 className="text-base font-medium text-gray-900 dark:text-white mb-2">
              Para acceder a: <span className="text-blue-600 dark:text-blue-400">{actionName}</span>
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {actionDescription}
            </p>
            
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                    Debes verificar tu email
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    Esta función maneja datos sensibles y requiere una cuenta verificada para garantizar la seguridad.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-lg text-center">
              {user.email}
            </div>
          </div>

          {/* Resend message */}
          {resendMessage && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">{resendMessage}</p>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleResendEmail}
              disabled={isResending}
              className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Enviando...</span>
                </div>
              ) : (
                'Reenviar Email de Verificación'
              )}
            </button>
            
            <button
              onClick={onClose}
              className="w-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-medium py-3 px-4 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
          </div>

          {/* Help text */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              Nota: Mientras tanto, puedes seguir explorando otras funciones de GuideMe
            </p>
          </div>
        </div>
    </BaseModal>
  );
};

export default EmailVerificationModal;